import { Lightbulb } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type ProjectFeedEvent,
  type ProjectSuggestionPayload,
  type ProjectSuggestionResolution,
} from "../../../services/askPunttProjectFeed.types";

/**
 * Proactive Puntt suggestion card with the three core actions — Ignore,
 * Approve, Adjust (§7). Approve fires the default server-side action; Ignore
 * persists a dismissal; Adjust reveals an inline note field whose submission
 * carries a payload back to the resolve endpoint. Once resolved the card
 * collapses to a status line so it can't be acted on twice.
 */
export function FeedSuggestionCard({
  event,
  onResolve,
  isResolving = false,
}: {
  event: ProjectFeedEvent;
  onResolve: (
    action: ProjectSuggestionResolution,
    payload?: Record<string, unknown>,
  ) => void;
  isResolving?: boolean;
}) {
  const { t } = useTranslation("ui");
  const payload = (event.payload ?? {}) as ProjectSuggestionPayload;
  const [adjusting, setAdjusting] = useState(false);
  const [adjustNote, setAdjustNote] = useState("");
  const titleId = `feed-suggestion-title-${event._id}`;

  if (payload.resolution) {
    return (
      <section
        className="rounded-xl border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 p-3"
        data-testid="project-feed-suggestion-card"
        data-resolution={payload.resolution}
        aria-labelledby={titleId}
      >
        <p
          id={titleId}
          className="text-[12.5px] text-puntt-neutral-gray-11"
          data-testid="project-suggestion-resolved"
        >
          {payload.resolution === "ignore"
            ? t("feedSuggestionCard.dismissed")
            : payload.resolution === "approve"
              ? t("feedSuggestionCard.approved")
              : t("feedSuggestionCard.adjusted")}
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-xl border border-puntt-blue-6 bg-base-white p-3.5"
      data-testid="project-feed-suggestion-card"
      aria-labelledby={titleId}
    >
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-puntt-blue-2 text-puntt-accent-9"
        >
          <Lightbulb size={16} weight="fill" />
        </span>
        <div className="min-w-0 flex-1">
          <h3
            id={titleId}
            className="text-[13px] font-semibold text-puntt-neutral-gray-12"
          >
            {event.body ?? t("feedSuggestionCard.defaultTitle")}
          </h3>
          {payload.reason && (
            <p
              className="mt-1 text-[12px] leading-relaxed text-puntt-neutral-gray-11"
              data-testid="project-suggestion-reason"
            >
              {payload.reason}
            </p>
          )}
        </div>
      </div>

      {adjusting ? (
        <div className="mt-3">
          <label
            htmlFor={`feed-suggestion-adjust-input-${event._id}`}
            className="block text-[11px] font-medium text-puntt-neutral-gray-11"
          >
            {t("feedSuggestionCard.adjustLabel")}
          </label>
          <textarea
            id={`feed-suggestion-adjust-input-${event._id}`}
            data-testid="project-suggestion-adjust-input"
            value={adjustNote}
            onChange={(e) => setAdjustNote(e.target.value)}
            placeholder={t("feedSuggestionCard.adjustPlaceholder")}
            className="mt-1 w-full resize-none rounded-md border border-puntt-neutral-gray-6 bg-base-white px-2.5 py-2 text-[13px] text-puntt-neutral-gray-12 outline-none focus:border-puntt-accent-9"
            rows={2}
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              data-testid="project-suggestion-adjust-cancel"
              aria-label={t("feedSuggestionCard.cancelAdjustmentAriaLabel")}
              onClick={() => {
                setAdjusting(false);
                setAdjustNote("");
              }}
              className="rounded-md px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3"
            >
              {t("feedSuggestionCard.cancel")}
            </button>
            <button
              type="button"
              data-testid="project-suggestion-adjust-submit"
              aria-label={t("feedSuggestionCard.submitAdjustmentAriaLabel")}
              disabled={isResolving || adjustNote.trim().length === 0}
              onClick={() => onResolve("adjust", { note: adjustNote.trim() })}
              className="rounded-md bg-puntt-accent-9 px-2.5 py-1 text-[12px] font-medium text-base-white hover:bg-puntt-accent-10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("feedSuggestionCard.saveAdjustment")}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            data-testid="project-suggestion-ignore"
            aria-label={t("feedSuggestionCard.ignoreAriaLabel")}
            disabled={isResolving}
            onClick={() => onResolve("ignore")}
            className="rounded-md border border-puntt-neutral-gray-6 px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("feedSuggestionCard.ignore")}
          </button>
          <button
            type="button"
            data-testid="project-suggestion-approve"
            aria-label={t("feedSuggestionCard.approveAriaLabel")}
            disabled={isResolving}
            onClick={() => onResolve("approve")}
            className="rounded-md bg-puntt-accent-9 px-2.5 py-1 text-[12px] font-medium text-base-white hover:bg-puntt-accent-10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("feedSuggestionCard.approve")}
          </button>
          <button
            type="button"
            data-testid="project-suggestion-adjust"
            aria-label={t("feedSuggestionCard.adjustAriaLabel")}
            disabled={isResolving}
            onClick={() => setAdjusting(true)}
            className="rounded-md border border-puntt-neutral-gray-6 px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("feedSuggestionCard.adjust")}
          </button>
        </div>
      )}
    </section>
  );
}
